import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch, apiUrl, resolveMediaUrl } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { inputClass, labelClass } from '@/components/ui/inputStyles';
import type { MicroPromoInfoType } from '@/components/MicroPromotionParamsPanel';
import type { MicroPromotionEntryDetail } from '@/components/MicroPromotionEntryDetailModal';

type Props = {
  open: boolean;
  areaId: number;
  infoType: MicroPromoInfoType;
  entryId?: number;
  onClose: () => void;
  onSaved: () => void;
  /** 站长手机端：精简字段 + 大触控 UI */
  siteMasterMode?: boolean;
};

const smFieldClass =
  'w-full rounded-2xl border-2 border-slate-200 px-4 py-3.5 text-lg focus:border-teal-500 focus:outline-none focus:ring-4 focus:ring-teal-500/20';
const smLabelClass = 'mb-2 block text-lg font-semibold text-slate-800';

const MP_PROMOTION_DAYS_MAX = 180;

const PROMOTION_DAY_PRESETS = [
  { label: '1天', days: 1 },
  { label: '1周', days: 7 },
  { label: '1个月', days: 30 },
  { label: '1季度', days: 90 },
  { label: '1年', days: 365 },
] as const;

function parsePromotionDaysInput(raw: string): number {
  const n = parseInt(raw.trim(), 10);
  if (!Number.isFinite(n) || n < 1) return 0;
  return Math.min(MP_PROMOTION_DAYS_MAX, n);
}

export function MicroPromotionEntryFormModal({
  open,
  areaId,
  infoType,
  entryId = 0,
  onClose,
  onSaved,
  siteMasterMode = false,
}: Props) {
  const isEdit = entryId > 0;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [displayText, setDisplayText] = useState('');
  const [promotionText, setPromotionText] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [salary, setSalary] = useState('');
  const [recruiter, setRecruiter] = useState('');
  const [jobDescription, setJobDescription] = useState('');
  const [totalDays, setTotalDays] = useState(7);
  const [totalDaysInput, setTotalDaysInput] = useState('7');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [channel, setChannel] = useState<'all' | 'moments' | 'groups'>('all');
  const [imagePaths, setImagePaths] = useState<string[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);

  const resetBlank = useCallback(() => {
    setDisplayText('');
    setPromotionText('');
    setContactPhone('');
    setJobTitle('');
    setSalary('');
    setRecruiter('');
    setJobDescription('');
    setTotalDays(7);
    setTotalDaysInput('7');
    setStartDate(new Date().toISOString().slice(0, 10));
    setChannel('all');
    setImagePaths([]);
    setImagePreviews([]);
    setMsg('');
  }, []);

  const loadEntry = useCallback(async () => {
    if (!open || entryId < 1) return;
    setLoading(true);
    setMsg('');
    try {
      const action = siteMasterMode ? 'site_master_get_mp_entry' : 'get_micro_promotion_entry';
      const r = await apiFetch<{ ok: boolean; entry?: MicroPromotionEntryDetail }>(action, {
        method: 'GET',
        query: { entry_id: entryId },
      });
      if (!r.ok || !r.entry) {
        setMsg('无法加载条目');
        return;
      }
      const e = r.entry;
      const promo = String(e.promotion_text ?? '').trim() || String(e.display_text ?? '').trim();
      setPromotionText(promo);
      setDisplayText(String(e.display_text ?? promo));
      setContactPhone(String(e.contact_phone ?? ''));
      setJobTitle(String(e.job_title ?? ''));
      setSalary(String(e.salary ?? ''));
      setRecruiter(String(e.recruiter ?? ''));
      setJobDescription(String(e.job_description ?? ''));
      setTotalDays(Number(e.total_promotion_days) || 7);
      setTotalDaysInput(String(Number(e.total_promotion_days) || 7));
      setStartDate(String(e.promotion_start_date ?? '').slice(0, 10) || new Date().toISOString().slice(0, 10));
      const ch = String(e.promotion_channel ?? 'all');
      setChannel(ch === 'moments' || ch === 'groups' ? ch : 'all');
      const paths = (e.images ?? []).map((im) => String(im.local_path || im.path || '').trim()).filter(Boolean);
      setImagePaths(paths);
      setImagePreviews(paths.map((p) => resolveMediaUrl(p)));
    } finally {
      setLoading(false);
    }
  }, [open, entryId, siteMasterMode]);

  useEffect(() => {
    if (!open) return;
    if (isEdit) void loadEntry();
    else resetBlank();
  }, [open, isEdit, loadEntry, resetBlank]);

  async function uploadImageFile(file: File) {
    if (!file || areaId < 1) return;
    if (imagePaths.length >= 9) {
      setMsg('最多 9 张图片');
      return;
    }
    setUploading(true);
    setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('area_id', String(areaId));
      fd.append('info_type', infoType);
      const action = siteMasterMode ? 'site_master_upload_mp_entry_image' : 'upload_micro_promotion_image';
      const xhr = new XMLHttpRequest();
      xhr.open('POST', apiUrl(action));
      xhr.withCredentials = true;
      const path = await new Promise<string>((resolve, reject) => {
        xhr.onload = () => {
          try {
            const j = JSON.parse(xhr.responseText) as { ok?: boolean; path?: string; err?: string };
            if (xhr.status >= 200 && xhr.status < 300 && j.ok && j.path) resolve(j.path);
            else reject(new Error(j.err || '上传失败'));
          } catch {
            reject(new Error('上传失败'));
          }
        };
        xhr.onerror = () => reject(new Error('网络错误'));
        xhr.send(fd);
      });
      setImagePaths((p) => [...p, path]);
      setImagePreviews((p) => [...p, resolveMediaUrl(path)]);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '上传失败');
    } finally {
      setUploading(false);
    }
  }

  async function onPickImage(file: File | null) {
    if (!file) return;
    await uploadImageFile(file);
  }

  async function onPickImages(files: FileList | null) {
    if (!files?.length) return;
    const remain = 9 - imagePaths.length;
    for (const file of Array.from(files).slice(0, remain)) {
      await uploadImageFile(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function removeImage(idx: number) {
    setImagePaths((p) => p.filter((_, i) => i !== idx));
    setImagePreviews((p) => p.filter((_, i) => i !== idx));
  }

  function validateSiteMaster(): boolean {
    if (infoType === 'graphic') {
      if (promotionText.trim() === '') {
        setMsg('请填写推广文案');
        return false;
      }
      if (imagePaths.length < 1) {
        setMsg('请至少上传 1 张图片');
        return false;
      }
    } else {
      if (jobTitle.trim() === '') {
        setMsg('请填写岗位名称');
        return false;
      }
    }
    if (contactPhone.trim() === '') {
      setMsg('请填写联系电话');
      return false;
    }
    const days = parsePromotionDaysInput(totalDaysInput);
    if (days < 1) {
      setMsg(`请填写推广天数（1–${MP_PROMOTION_DAYS_MAX} 天）`);
      return false;
    }
    return true;
  }

  async function submit() {
    if (siteMasterMode && !validateSiteMaster()) return;

    setSaving(true);
    setMsg('');

    const promo = promotionText.trim();
    const siteMasterDays = parsePromotionDaysInput(totalDaysInput);
    const payload = siteMasterMode
      ? {
          area_id: areaId,
          info_type: infoType,
          display_text: infoType === 'graphic' ? promo : jobTitle.trim(),
          promotion_text: infoType === 'graphic' ? promo : '',
          contact_phone: contactPhone.trim(),
          job_title: jobTitle.trim(),
          salary: salary.trim(),
          recruiter: recruiter.trim(),
          job_description: jobDescription.trim(),
          total_promotion_days: siteMasterDays,
          promotion_start_date: startDate,
          promotion_channel: channel,
          image_local_paths: imagePaths,
          ...(isEdit ? { entry_id: entryId } : {}),
        }
      : {
          area_id: areaId,
          info_type: infoType,
          display_text: displayText.trim(),
          promotion_text: promo,
          contact_phone: contactPhone.trim(),
          job_title: jobTitle.trim(),
          salary: salary.trim(),
          recruiter: recruiter.trim(),
          job_description: jobDescription.trim(),
          total_promotion_days: totalDays,
          promotion_start_date: startDate,
          promotion_channel: channel,
          image_local_paths: imagePaths,
          ...(isEdit ? { entry_id: entryId } : {}),
        };

    try {
      const action = siteMasterMode
        ? isEdit
          ? 'site_master_update_mp_entry'
          : 'site_master_create_mp_entry'
        : isEdit
          ? 'update_micro_promotion_entry'
          : 'create_micro_promotion_entry';
      const r = await apiFetch<{ ok: boolean; err?: string; detail?: string }>(action, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        setMsg(r.detail ?? r.err ?? '保存失败');
        return;
      }
      onSaved();
      onClose();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }

  const title = siteMasterMode
    ? isEdit
      ? `编辑${infoType === 'graphic' ? '图文' : '招聘'}推广`
      : `添加${infoType === 'graphic' ? '图文' : '招聘'}推广`
    : isEdit
      ? `编辑推广信息 #${entryId}`
      : '添加推广信息';

  const busy = saving || loading || uploading;

  return (
    <Modal
      open={open}
      onClose={onClose}
      wide={siteMasterMode ? undefined : 'xl'}
      title={title}
      footer={
        siteMasterMode ? undefined : (
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
              取消
            </Button>
            <Button type="button" variant="primary" onClick={() => void submit()} disabled={busy}>
              {saving ? '保存中…' : '保存'}
            </Button>
          </div>
        )
      }
    >
      {loading ? (
        <Spinner label="加载中" />
      ) : siteMasterMode ? (
        <div className="space-y-5">
          {msg ? (
            <p className="rounded-2xl border-2 border-rose-200 bg-rose-50 px-4 py-3 text-base text-rose-900" role="alert">
              {msg}
            </p>
          ) : null}

          {infoType === 'graphic' ? (
            <>
              <label className="block">
                <span className={smLabelClass}>
                  推广文案
                  <span className="ml-1 text-base font-normal text-red-600">*</span>
                </span>
                <p className="mb-2 text-base text-slate-500">机器人发朋友圈/群发时将按区域模板展开此内容</p>
                <textarea
                  className={cn(smFieldClass, 'min-h-[9rem] resize-y leading-relaxed')}
                  value={promotionText}
                  onChange={(e) => setPromotionText(e.target.value)}
                  placeholder="例如：香山壹境，80平，2楼，2房"
                  required
                />
              </label>

              <div>
                <div className="mb-2 flex items-baseline justify-between gap-2">
                  <span className={smLabelClass}>
                    图片
                    <span className="ml-1 text-base font-normal text-red-600">*</span>
                  </span>
                  <span className="text-base text-slate-500">
                    {imagePaths.length}/9
                  </span>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => void onPickImages(e.target.files)}
                />
                {imagePaths.length < 9 ? (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => fileInputRef.current?.click()}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 py-4 text-lg font-semibold text-slate-700 disabled:opacity-50"
                  >
                    <i className="fa-solid fa-images text-xl" aria-hidden />
                    {uploading ? '上传中…' : '选择图片'}
                  </button>
                ) : null}
                {imagePreviews.length > 0 ? (
                  <ul className="mt-3 grid grid-cols-3 gap-2">
                    {imagePreviews.map((src, i) => (
                      <li key={`${src}-${i}`} className="relative aspect-square overflow-hidden rounded-xl border border-slate-200">
                        <img src={src} alt="" className="h-full w-full object-cover" />
                        <button
                          type="button"
                          className="absolute right-1 top-1 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-lg font-bold text-white"
                          aria-label="移除图片"
                          onClick={() => removeImage(i)}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </>
          ) : (
            <>
              <label className="block">
                <span className={smLabelClass}>
                  岗位名称
                  <span className="ml-1 text-base font-normal text-red-600">*</span>
                </span>
                <input
                  className={smFieldClass}
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="例如：普工、服务员"
                />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className={smLabelClass}>工资</span>
                  <input
                    className={smFieldClass}
                    value={salary}
                    onChange={(e) => setSalary(e.target.value)}
                    placeholder="如 5000-8000"
                  />
                </label>
                <label className="block">
                  <span className={smLabelClass}>招聘方</span>
                  <input className={smFieldClass} value={recruiter} onChange={(e) => setRecruiter(e.target.value)} />
                </label>
              </div>
              <label className="block">
                <span className={smLabelClass}>工作内容</span>
                <textarea
                  className={cn(smFieldClass, 'min-h-[7rem] resize-y leading-relaxed')}
                  value={jobDescription}
                  onChange={(e) => setJobDescription(e.target.value)}
                  placeholder="岗位职责、要求等"
                />
              </label>
            </>
          )}

          <label className="block">
            <span className={smLabelClass}>
              联系电话
              <span className="ml-1 text-base font-normal text-red-600">*</span>
            </span>
            <input
              className={smFieldClass}
              type="tel"
              inputMode="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="买家/求职者可直接拨打"
              autoComplete="tel"
            />
          </label>

          <section className="rounded-2xl border-2 border-slate-200 bg-slate-50/50 p-4">
            <h3 className={smLabelClass}>推广设置</h3>
            <div className="mt-3 space-y-4">
              <div>
                <span className="mb-2 block text-base font-medium text-slate-700">推广天数</span>
                <div className="mb-3 flex flex-wrap gap-2">
                  {PROMOTION_DAY_PRESETS.map((preset) => {
                    const active = totalDaysInput === String(preset.days);
                    return (
                      <button
                        key={preset.label}
                        type="button"
                        className={cn(
                          'rounded-full border-2 px-4 py-2 text-base font-semibold transition active:scale-[0.98]',
                          active
                            ? 'border-teal-600 bg-teal-50 text-teal-900'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300',
                        )}
                        onClick={() => setTotalDaysInput(String(preset.days))}
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                </div>
                <label className="block">
                  <span className="mb-2 block text-sm text-slate-500">自定义天数</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    className={smFieldClass}
                    value={totalDaysInput}
                    placeholder="例如 1、15、60"
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, '').slice(0, 3);
                      setTotalDaysInput(v);
                    }}
                  />
                </label>
                <p className="mt-2 text-sm text-slate-500">
                  可点选常用时长，也可手动输入；范围 1–{MP_PROMOTION_DAYS_MAX} 天（超出按 {MP_PROMOTION_DAYS_MAX} 天保存）
                </p>
              </div>
              <label className="block">
                <span className="mb-2 block text-base font-medium text-slate-700">开始日期</span>
                <input
                  type="date"
                  className={smFieldClass}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-2 block text-base font-medium text-slate-700">推广渠道</span>
                <select
                  className={cn(smFieldClass, 'appearance-none bg-white')}
                  value={channel}
                  onChange={(e) => setChannel(e.target.value as typeof channel)}
                >
                  <option value="all">朋友圈 + 微信群</option>
                  <option value="moments">仅朋友圈</option>
                  <option value="groups">仅微信群</option>
                </select>
              </label>
            </div>
          </section>

          <button
            type="button"
            disabled={busy}
            onClick={() => void submit()}
            className="w-full rounded-2xl bg-teal-600 py-4 text-xl font-bold text-white shadow-lg disabled:opacity-60"
          >
            {saving ? '保存中…' : isEdit ? '保存修改' : '提交推广'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {msg ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">{msg}</p> : null}
          <p className="text-sm text-slate-600">
            {infoType === 'graphic' ? '图文信息' : '招聘信息'} · 区域 #{areaId} · 后台添加
          </p>

          {infoType === 'recruitment' ? (
            <>
              <label className={labelClass}>
                <span>职位名称</span>
                <input className={inputClass} value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className={labelClass}>
                  <span>薪资</span>
                  <input className={inputClass} value={salary} onChange={(e) => setSalary(e.target.value)} placeholder="如：5000-8000" />
                </label>
                <label className={labelClass}>
                  <span>招聘方</span>
                  <input className={inputClass} value={recruiter} onChange={(e) => setRecruiter(e.target.value)} />
                </label>
              </div>
              <label className={labelClass}>
                <span>职位描述</span>
                <textarea className={`${inputClass} min-h-[80px]`} value={jobDescription} onChange={(e) => setJobDescription(e.target.value)} />
              </label>
            </>
          ) : null}

          <label className={labelClass}>
            <span>{infoType === 'graphic' ? '展示文案' : '展示摘要'}</span>
            <textarea className={`${inputClass} min-h-[72px]`} value={displayText} onChange={(e) => setDisplayText(e.target.value)} />
          </label>
          <label className={labelClass}>
            <span>推广文案（选填）</span>
            <textarea className={`${inputClass} min-h-[72px]`} value={promotionText} onChange={(e) => setPromotionText(e.target.value)} />
          </label>
          <label className={labelClass}>
            <span>联系电话</span>
            <input className={inputClass} value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} inputMode="tel" />
          </label>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className={labelClass}>
              <span>推广天数</span>
              <input
                type="number"
                min={1}
                max={180}
                className={inputClass}
                value={totalDays}
                onChange={(e) => setTotalDays(parseInt(e.target.value, 10) || 7)}
              />
            </label>
            <label className={labelClass}>
              <span>开始日期</span>
              <input type="date" className={inputClass} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label className={labelClass}>
              <span>推广渠道</span>
              <select className={inputClass} value={channel} onChange={(e) => setChannel(e.target.value as typeof channel)}>
                <option value="all">朋友圈 + 微信群</option>
                <option value="moments">仅朋友圈</option>
                <option value="groups">仅微信群</option>
              </select>
            </label>
          </div>

          {infoType === 'graphic' ? (
            <div>
              <span className={labelClass}>图片（至少 1 张，最多 9 张）</span>
              <input
                type="file"
                accept="image/*"
                className="mt-1 block text-sm"
                disabled={uploading}
                onChange={(e) => void onPickImage(e.target.files?.[0] ?? null)}
              />
              {imagePreviews.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {imagePreviews.map((src, i) => (
                    <div key={`${src}-${i}`} className="relative">
                      <img src={src} alt="" className="h-20 w-20 rounded-lg border object-cover" />
                      <button
                        type="button"
                        className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-rose-600 text-xs text-white"
                        onClick={() => removeImage(i)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
